import React, { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';

// private route
import PrivateRoute from 'routes/PrivateRoute';

// pages
import PartnerCreate from 'pages/partners/create';
import Partners from 'pages/partners';

import WineList from 'pages/wineLists';
import WineListCreate from 'pages/wineLists/create';
import Wines from 'pages/wines';
import WineLibraryBrowse from 'pages/wines/wineLibraryBrowse';
import WineCreate from 'pages/wines/create';
import WineLabels from 'pages/wineLabels';
import WineLabelsCreate from 'pages/wineLabels/create';

import Users from 'pages/users/index';

import Settings from 'pages/settings';
import Login from 'pages/login';
import Signup from 'pages/signup';
import Profile from 'pages/profile';
import Dashboard from 'pages/dashboard/index';
import NotFound from 'pages/notFound';
import Home from 'pages/welcome';

import ForgotPassword from 'pages/forgotPassword';
import ResetPassword from 'pages/resetPassword';
import ChangePassword from 'pages/changePassword';

import SubscriptionResponse from 'pages/subscriptions/subscriptionResponse';
import Subscriptions from 'pages/subscriptions';

import WineLovers from 'pages/wineLovers';
import WineLover from 'pages/wineLovers/wineLover';

import WineLoversActivities from 'pages/wineLoversActivities';
import WineLoverActivity from 'pages/wineLoversActivities/wineLoverActivity';
import Campaigns from 'pages/campaigns';
import CampaignForm from 'pages/campaigns/create';

import Tasteimonials from 'pages/tasteimonials/index.jsx';

import { setupInterceptors } from 'services/client/axiosHelper';
import domainData from 'store/domainStore';
import { isSuperAdmin } from 'helpers/usersRolesHelper';
import { getMessage } from 'helpers/helper';

import { routes } from './config/index';
import './App.css';

const useStyles = () => ({
  root: {
    display: 'block',
  },
});

const App = observer(() => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  setupInterceptors(location, navigate);
  const currentUser = domainData.getCurrentUser();
  const isUserSuperAdmin = isSuperAdmin(currentUser);
  const checkAccess = useCallback(async () => {
    try {
      await domainData.checkSubscriptionAccess(isUserSuperAdmin);
    } catch (err) {
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, isUserSuperAdmin]);
  useEffect(() => {
    if (currentUser?.id) {
      checkAccess();
    }
  }, [checkAccess, currentUser?.id]);

  return (
    <div style={classes.root}>
      <Routes>
        <Route path={routes.login.path} exact element={<Login />} />
        <Route path={routes.signUp.path} exact element={<Signup />} />
        <Route path={routes.forgotPassword.path} exact element={<ForgotPassword />} />
        <Route path={routes.resetPassword.path} exact element={<ResetPassword />} />
        <Route path={routes.inviteUser.path} exact element={<ResetPassword />} />
        <Route path={routes.changePassword.path} exact element={<ChangePassword />} />
        <Route exact path={routes.home.path} element={<Navigate to={routes.welcome.path} />} />
        <Route
          exact
          path={routes.welcome.path}
          element={<PrivateRoute component={Home} path={routes.welcome.path} />}
        />
        {currentUser?.serviceProviderId && (
          <Route
            exact
            path={routes.serviceProviderCreate.path}
            element={
              <PrivateRoute
                component={PartnerCreate}
                to={routes.serviceProviderEdit.path.replace(':id', currentUser.serviceProviderId)}
              />
            }
          />
        )}
        <Route
          index
          exact
          path={routes.settings.path}
          element={<PrivateRoute component={Settings} path={routes.settings.path} />}
        />
        <Route
          index
          exact
          path={routes.dashboard.path}
          element={<PrivateRoute component={Dashboard} path={routes.dashboard.path} />}
        />
        {isUserSuperAdmin && (
          <Route exact path={routes.users.path} element={<PrivateRoute component={Users} path={routes.users.path} />} />
        )}
        <Route
          exact
          path={routes.userEdit.path}
          element={<PrivateRoute component={Profile} path={routes.userEdit.path} isEdit />}
        />
        <Route
          exact
          path={routes.profileEdit.path}
          element={<PrivateRoute component={Profile} path={routes.profileEdit.path} isProfile />}
        />
        <Route
          exact
          path={routes.serviceProviderCreate.path}
          element={<PrivateRoute component={PartnerCreate} path={routes.serviceProviderCreate.path} isCreate />}
        />
        <Route
          exact
          path={routes.serviceProviderEdit.path}
          element={<PrivateRoute component={PartnerCreate} path={routes.serviceProviderEdit.path} isEdit />}
        />
        <Route
          exact
          path={routes.serviceProviders.path}
          element={<PrivateRoute component={Partners} path={routes.serviceProviders.path} />}
        />

        <Route
          exact
          path={routes.wineListsCreate.path}
          element={<PrivateRoute component={WineListCreate} path={routes.wineListsCreate.path} isCreate />}
        />
        <Route
          exact
          path={routes.wineListsEdit.path}
          element={<PrivateRoute component={WineListCreate} path={routes.wineListsEdit.path} isEdit />}
        />
        <Route
          exact
          path={routes.wineLists.path}
          element={<PrivateRoute component={WineList} path={routes.wineLists.path} />}
        />

        <Route
          exact
          path={routes.wineCreate.path}
          element={<PrivateRoute component={WineCreate} path={routes.wineCreate.path} isCreate />}
        />
        <Route
          exact
          path={routes.wineEdit.path}
          element={<PrivateRoute component={WineCreate} path={routes.wineEdit.path} isEdit />}
        />
        <Route exact path={routes.wines.path} element={<PrivateRoute component={Wines} path={routes.wines.path} />} />
        <Route
          exact
          path={routes.wineLibraryBrowse.path}
          element={<PrivateRoute component={WineLibraryBrowse} path={routes.wineLibraryBrowse.path} />}
        />

        <Route
          exact
          path={routes.wineLabelsCreate.path}
          element={<PrivateRoute component={WineLabelsCreate} path={routes.wineLabelsCreate.path} isCreate />}
        />
        <Route
          exact
          path={routes.wineLabelsEdit.path}
          element={<PrivateRoute component={WineLabelsCreate} path={routes.wineLabelsEdit.path} isEdit />}
        />
        <Route
          exact
          path={routes.wineLabels.path}
          element={<PrivateRoute component={WineLabels} path={routes.wineLabels.path} />}
        />

        <Route
          exact
          path={routes.subscriptions.path}
          element={<PrivateRoute component={Subscriptions} path={routes.subscriptions.path} />}
        />
        <Route
          exact
          path={routes.subscription.path}
          element={<PrivateRoute component={SubscriptionResponse} path={routes.subscription.path} />}
        />

        <Route
          exact
          path={routes.wineLovers.path}
          element={<PrivateRoute component={WineLovers} path={routes.wineLovers.path} />}
        />
        <Route
          exact
          path={routes.wineLover.path}
          element={<PrivateRoute component={WineLover} path={routes.wineLover.path} isEdit />}
        />

        <Route
          exact
          path={routes.wineLoversActivities.path}
          element={<PrivateRoute component={WineLoversActivities} path={routes.wineLoversActivities.path} />}
        />
        <Route
          exact
          path={routes.wineLoverActivity.path}
          element={<PrivateRoute component={WineLoverActivity} path={routes.wineLoverActivity.path} />}
        />

        <Route
          exact
          path={routes.tasteimonials.path}
          element={<PrivateRoute component={Tasteimonials} path={routes.tasteimonials.path} />}
        />

        <Route
          exact
          path={routes.campaigns.path}
          element={<PrivateRoute component={Campaigns} path={routes.campaigns.path} />}
        />
        <Route
          exact
          path={routes.campaignsCreate.path}
          element={<PrivateRoute component={CampaignForm} path={routes.campaignsCreate.path} isCreate />}
        />
        <Route
          exact
          path={routes.campaignsEdit.path}
          element={<PrivateRoute component={CampaignForm} path={routes.campaignsEdit.path} isEdit />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
});

export default App;
